import React from 'react';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';

const faqs = [
  { q: '¿Qué son los libritos del Museo de la Fe?', a: 'Son libritos cristianos ilustrados diseñados especialmente para niños de 3 a 10 años. Cada librito cuenta una historia bíblica con ilustraciones coloridas y un lenguaje sencillo que los pequeños pueden entender y disfrutar.' },
  { q: '¿Para qué edad son los libritos cristianos ilustrados?', a: 'Nuestros libritos están diseñados para niños de 3 a 10 años. Las ilustraciones y el lenguaje están adaptados para que sean accesibles desde los más pequeños hasta niños en edad escolar.' },
  { q: '¿Los libritos están disponibles en formato digital?', a: 'Sí, todos nuestros libritos están disponibles en formato digital interactivo (flipbook). Una vez que compras un librito, puedes leerlo directamente en nuestra plataforma desde cualquier dispositivo con internet.' },
  { q: '¿Cómo comprar libritos cristianos para niños?', a: 'Navega nuestro catálogo, agrega los libritos que te gusten al carrito, y paga de forma segura con PayPal o tarjeta. Una vez comprado, el librito aparece en tu biblioteca personal para leerlo cuando quieras.' },
  { q: '¿Cuánto cuestan los libritos cristianos ilustrados?', a: 'Nuestros libritos tienen precios accesibles desde $2.99 USD. También ofrecemos descuentos especiales cuando compras 2 o más libritos juntos, con un 25% de ahorro.' },
  { q: '¿Son aptos para escuela dominical e iglesias?', a: 'Absolutamente. Son perfectos para maestros de escuela dominical, pastores, ministerios infantiles y cualquier persona que quiera enseñar valores bíblicos a los niños de una forma atractiva y memorable.' },
  { q: '¿Puedo leer los libritos en mi celular o tablet?', a: 'Sí, nuestra plataforma es 100% responsiva. Puedes leer los libritos en tu computadora, tablet o celular. El formato flipbook se adapta a cualquier tamaño de pantalla.' },
  { q: '¿Está disponible para Latinoamérica y Estados Unidos?', a: 'Sí, nuestros libritos digitales están disponibles para toda Latinoamérica y Estados Unidos. Al ser formato digital, no hay costos de envío ni tiempos de espera.' },
  { q: '¿Cómo enseñar la fe a los niños con cuentos?', a: 'Las historias bíblicas son una de las formas más efectivas de transmitir la fe a los niños. Nuestros libritos presentan cada historia con ilustraciones que capturan la imaginación y un lenguaje que conecta con los pequeños, haciendo que las enseñanzas bíblicas sean memorables y significativas.' },
];

const FAQPage = () => {
  return (
    <div className="min-h-dvh bg-background font-sans">
      <Helmet>
        <title>Preguntas Frecuentes — Libritos Cristianos para Niños | Museo de la Fe</title>
        <meta name="description" content="Respuestas a las preguntas más frecuentes sobre los libritos cristianos ilustrados para niños del Museo de la Fe. Precios, edades, formato digital y más." />
        <meta property="og:title" content="Preguntas Frecuentes — Museo de la Fe" />
        <meta property="og:description" content="Respuestas a las preguntas más frecuentes sobre los libritos cristianos ilustrados para niños." />
        <meta property="og:url" content="https://museodelafe.com/preguntas-frecuentes" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Preguntas Frecuentes — Museo de la Fe" />
        <meta name="twitter:description" content="Respuestas a las preguntas más frecuentes sobre los libritos cristianos ilustrados para niños." />
        <link rel="canonical" href="https://museodelafe.com/preguntas-frecuentes" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://museodelafe.com" },
            { "@type": "ListItem", "position": 2, "name": "Preguntas Frecuentes", "item": "https://museodelafe.com/preguntas-frecuentes" }
          ]
        })}</script>
      </Helmet>
      <Header />

      <div className="max-w-xl mx-auto px-6 pt-10 pb-24">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 30, filter: 'blur(6px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="text-lg font-bold">Preguntas Frecuentes</h1>
          <p className="text-xs text-muted-foreground/50 mt-1">Todo lo que necesitas saber sobre nuestros libritos</p>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.details
              key={faq.q}
              className="group bg-secondary/30 hover:bg-secondary/50 rounded-xl transition-colors overflow-hidden"
              initial={{ opacity: 0, y: 30, filter: 'blur(6px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{
                duration: 0.6,
                delay: 0.15 + i * 0.08,
                ease: [0.22, 1, 0.36, 1]
              }}
            >
              <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium hover:text-foreground/80 transition-colors list-none select-none">
                <span className="flex items-center gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-foreground/5 flex items-center justify-center text-[10px] font-bold text-muted-foreground/40">{i + 1}</span>
                  {faq.q}
                </span>
                <svg className="size-4 text-muted-foreground/30 group-open:rotate-180 transition-transform duration-300 shrink-0 ml-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
              </summary>
              <div className="px-5 pb-4 pl-14">
                <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
              </div>
            </motion.details>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;
